import { ephemeralRouteParamKeys } from '@aline/routes';
import merge from 'lodash/merge.js';

const EPHEMERAL_KEY_SET = new Set<string>(ephemeralRouteParamKeys);

function getIsEphemeralParamKey(key: string): boolean {
  return EPHEMERAL_KEY_SET.has(key);
}

function parseRouteParts(route: string): {
  hash: string;
  pathname: string;
  search: string;
} {
  const hashIndex = route.indexOf('#');
  const hash = hashIndex >= 0 ? route.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? route.slice(0, hashIndex) : route;
  const searchIndex = withoutHash.indexOf('?');
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  return { hash, pathname, search };
}

function serializeRoute(pathname: string, searchParams: URLSearchParams, hash: string): string {
  const search = searchParams.toString();
  return `${pathname}${search ? `?${search}` : ''}${hash}`;
}

/**
 * Splits a full route (pathname + search + hash) into a durable route (no one-shot deep-link
 * params) and the ephemeral params that should live only until consumed.
 */
export function splitRouteParams(route: string): {
  durableRoute: string;
  pendingParams: Record<string, string>;
} {
  const { hash, pathname, search } = parseRouteParts(route);
  const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const pendingParams: Record<string, string> = {};

  for (const key of [...searchParams.keys()]) {
    if (!getIsEphemeralParamKey(key)) continue;

    const value = searchParams.get(key);
    if (value === null) continue;

    pendingParams[key] = value;
    searchParams.delete(key);
  }

  return {
    durableRoute: serializeRoute(pathname, searchParams, hash),
    pendingParams,
  };
}

/**
 * Merges pending one-shot params into a location `search` string (with or without a leading `?`)
 * so `useLocation()` consumers still see them until they are consumed.
 */
export function mergePendingParamsIntoSearch(
  search: string,
  pendingParams?: Record<string, string>
): string {
  if (!pendingParams || Object.keys(pendingParams).length === 0) return search;

  const searchParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const [key, value] of Object.entries(pendingParams)) {
    searchParams.set(key, value);
  }
  const merged = searchParams.toString();
  return merged ? `?${merged}` : '';
}

/** Removes one pending key; returns `undefined` when nothing remains. */
export function omitPendingParam(
  pendingParams: Record<string, string> | undefined,
  key: string
): Record<string, string> | undefined {
  if (!pendingParams || !(key in pendingParams)) return pendingParams;

  const { [key]: _removed, ...rest } = pendingParams;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Merges newly arrived ephemeral params into existing pending. Unconsumed keys the incoming
 * route says nothing about are kept, but a key present on both sides takes the incoming value:
 * a newer deep link into an already-open tab must not lose to a param nobody consumed yet.
 */
export function mergePendingParams(
  existing: Record<string, string> | undefined,
  incoming: Record<string, string>
): Record<string, string> | undefined {
  if (Object.keys(incoming).length === 0) return existing;

  const merged = merge({}, existing, incoming);
  return Object.keys(merged).length > 0 ? merged : undefined;
}

/** Merges pending one-shot params into an already-durable route's search string. */
export function appendPendingParamsToRoute(
  route: string,
  pendingParams?: Record<string, string>
): string {
  if (!pendingParams || Object.keys(pendingParams).length === 0) return route;

  const { hash, pathname, search } = parseRouteParts(route);
  return `${pathname}${mergePendingParamsIntoSearch(search, pendingParams)}${hash}`;
}
